import { Inventory, Quiz } from '@components/Icons';
import { BasePath } from '@constants/Auth';
import { t } from '@lingui/macro';
import { Box, FormControl, makeStyles, MenuItem, Select, useMediaQuery, useTheme } from '@material-ui/core';
import { AssessmentOutlined, DescriptionOutlined, Group, ImportContactsOutlined, Settings } from '@material-ui/icons';
import { SafeAny } from '@models/common';
import { SettingTabs } from '@models/settings';
import { DeviceErrorPage } from '@pages/error/DeviceErrorPage';
import { cancelUrl, successUrl } from '@pages/settings/components/Billing';
import { FeatureNames, MainContent, MainSideMenu, Menu, Menus, useCollapsed } from '@swyft/ui';
import { useAppContext } from '@utils/appState';
import { useFeature } from '@utils/config';
import React, { ReactElement } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { Layout, LayoutProps } from '../models';
import { MainNavMenu } from './MainNavMenu';

const urlDirect = `${BasePath}/settings?activeTab=${SettingTabs.Billing}`;
const urlErrorPage = `${BasePath}/error_page`;

export const MainLayout: Layout = ({ children }: LayoutProps): ReactElement => {
  const {
    state: {
      companyInformation: { isCompanySuspended, isEnabledSubscription },
      tenantList
    }
  } = useAppContext();

  const [collapsed] = useCollapsed();
  const classes = useStyles();
  const recentTenant = window.location.host;
  const [tenantName, setTenantName] = React.useState<string>(recentTenant);

  const { enabled: contactFeatureEnabled } = useFeature(FeatureNames.Contact);
  const { enabled: quoteFeatureEnabled } = useFeature(FeatureNames.Quote);
  const { enabled: analyticFeatureEnabled } = useFeature(FeatureNames.Analytics);

  const items: Menus = [
    {
      path: BasePath + '/leads',
      title: t`Leads`,
      icon: Group
    },
    {
      path: contactFeatureEnabled ? BasePath + '/contacts' : '',
      title: t`Contacts`,
      icon: ImportContactsOutlined
    },
    {
      path: quoteFeatureEnabled ? BasePath + '/quotes' : '',
      title: t`Quotes`,
      icon: DescriptionOutlined
    },
    {
      path: BasePath + '/products',
      title: t`Products`,
      icon: Inventory
    },
    {
      path: BasePath + '/questionnaires',
      title: t`Questionnaires`,
      icon: Quiz
    },
    {
      path: analyticFeatureEnabled ? BasePath + '/analytics-reports' : '',
      title: t`Analytics Reports`,
      icon: AssessmentOutlined
    },
    // {
    //   path: 'https://www.pipelyne.com/faq',
    //   title: t`Help Centre`,
    //   icon: InfoOutlined,
    //   isNewTab: true
    // },
    {
      path: BasePath + '/settings',
      title: t`Settings`,
      icon: Settings
    }
  ];

  const adminMenu = items.filter((item: Menu) => item.path);

  const { pathname, search } = useLocation();
  const isRedirect = ![urlDirect, successUrl, cancelUrl].includes(pathname + search);

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  if (isMdDown) {
    return <DeviceErrorPage />;
  }

  const handleChangeTenant = (event: SafeAny) => {
    setTenantName(event.target.value);
    window.location.href = `https://${event.target.value}/admin/leads`;
  };

  return (
    <div className='flex w-full h-full overflow-hidden'>
      {isCompanySuspended === true && <Redirect to={urlErrorPage} />}
      {isEnabledSubscription === false && isRedirect && <Redirect to={urlDirect} />}
      <MainNavMenu />
      <MainSideMenu items={adminMenu} disabled={!isEnabledSubscription}>
        <Box pt={2} px={collapsed ? 0.3 : 3}>
          <FormControl fullWidth disabled={!tenantList?.length || tenantList?.length === 1}>
            <Select
              value={tenantName}
              disableUnderline
              onChange={(e) => handleChangeTenant(e)}
              classes={{ select: classes.select }}
            >
              {tenantList?.map((tenant, index) => (
                <MenuItem key={index} value={tenant.hostname}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </MainSideMenu>
      <MainContent>
        <Box
          mx='auto'
          p={{ xs: 2, lg: 3 }}
          display={'flex'}
          flexDirection={'column'}
          height={'100%'}
          maxWidth={{ xs: 'calc(100% - 70px)' }}
        >
          {children}
        </Box>
      </MainContent>
    </div>
  );
};

const useStyles = makeStyles({
  select: {
    margin: 0
  }
});
